import React from 'react';
import PropTypes from 'prop-types';
import { Textfit } from 'react-textfit';

export class EstimationTile extends React.PureComponent {
  static propTypes = {
    primaryIncrease: PropTypes.string.isRequired,
    primaryPts: PropTypes.string.isRequired,
    primaryFrom: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
    ]),
    primaryTo: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
    ]),
    primaryFromString: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
    ]),
    primaryToString: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
    ]),
    primaryString: PropTypes.string.isRequired,
    isPositivePrimary: PropTypes.bool.isRequired,
    isDoublePrimary: PropTypes.bool,
    secondPrimaryString: PropTypes.string,
    secondPrimaryIncrease: PropTypes.string,
    secondaryIncrease: PropTypes.string,
    secondaryString: PropTypes.string,
    isPositiveSecondary: PropTypes.bool,
    tertiaryIncrease: PropTypes.string,
    tertiaryString: PropTypes.string,
    spendIncrease: PropTypes.string,
    spendString: PropTypes.string,
    estimateNote: PropTypes.string.isRequired,
    primaryIncreaseSymbol: PropTypes.string,
    primaryIncreaseNullable: PropTypes.bool,
    isEstimate: PropTypes.bool,
  };

  static defaultProps = {
    primaryFrom: undefined,
    primaryTo: undefined,
    primaryFromString: undefined,
    primaryToString: undefined,
    secondaryIncrease: undefined,
    secondaryString: undefined,
    isPositiveSecondary: undefined,
    tertiaryIncrease: undefined,
    tertiaryString: undefined,
    spendIncrease: undefined,
    spendString: undefined,
    primaryIncreaseSymbol: '+',
    primaryIncreaseNullable: undefined,
    isDoublePrimary: false,
    secondPrimaryString: '',
    secondPrimaryIncrease: '',
    isEstimate: true,
  };

  constructor(props) {
    super(props);

    this.state = { primaryAndSecondPrimaryFontSize: undefined };
    this.isPrimaryReady = false;
    this.isSecondPrimaryReady = false;
    this.primaryFontSize = null;
    this.secondPrimaryFontSize = null;
  }

  getSecondarySymbol(value) {
    let symbol = '+';
    if (Number(value) < 0 || value.includes('-')) {
      symbol = '';
    }
    return symbol;
  }

  primaryReady(fontSize) {
    this.isPrimaryReady = true;
    this.primaryFontSize = fontSize;

    if (this.isSecondPrimaryReady) {
      this.fitPrimaryAndSecondPrimary();
    }
  }

  secondPrimaryReady(fontSize) {
    this.isSecondPrimaryReady = true;
    this.secondPrimaryFontSize = fontSize;

    if (this.isPrimaryReady) {
      this.fitPrimaryAndSecondPrimary();
    }
  }

  fitPrimaryAndSecondPrimary() {
    this.isPrimaryReady = false;
    this.isSecondPrimaryReady = false;
    if (this.primaryFontSize !== this.secondPrimaryFontSize) {
      const minFontSize = this.primaryFontSize > this.secondPrimaryFontSize ?
        this.secondPrimaryFontSize : this.primaryFontSize;

      this.setState({ primaryAndSecondPrimaryFontSize: minFontSize });
    }
  }

  render() {
    const {
      primaryFromString,
      primaryFrom,
      primaryToString,
      primaryTo,
      primaryIncreaseSymbol,
      primaryIncrease,
      primaryIncreaseNullable,
      primaryPts,
      primaryString,
      isPositivePrimary,
      secondaryIncrease,
      secondaryString,
      isPositiveSecondary,
      tertiaryIncrease,
      tertiaryString,
      spendIncrease,
      spendString,
      isDoublePrimary,
      secondPrimaryString,
      secondPrimaryIncrease,
      isEstimate,
    } = this.props;
    const fromTo = `${primaryFromString} ${primaryFrom} ${primaryToString} ${primaryTo}`;
    const primaryIncreaseStr = `${primaryIncreaseSymbol}${primaryIncrease} ${primaryPts}`;
    const noPrimaryIncrease = primaryIncreaseNullable && primaryIncrease === '0';
    const noPrimaryIncreaseClass = 'iconba large iconba-Trending12';
    const isLongText = primaryIncreaseStr.length > 9;

    return (
      <div className="recommendation-estimation-tile">
        <div className={`estimate-primary-titile ${isPositivePrimary ? 'positive-estimate' : 'neutral-estimate'}`}>
          <Textfit
            forceSingleModeWidth={false}
            max={this.state.primaryAndSecondPrimaryFontSize}
            onReady={fontSize => this.primaryReady(fontSize)}
          >{primaryString}
          </Textfit>
        </div>
        <div className={`estimate-primary-increase ${isPositivePrimary ? 'positive-estimate' : 'neutral-estimate'} ${isLongText && 'long-text'}`}>
          {noPrimaryIncrease ?
            (<span className={noPrimaryIncreaseClass} />)
            : (<Textfit mode="single" forceSingleModeWidth={false}>{primaryIncreaseStr}</Textfit>)}
        </div>
        {(primaryFrom && !noPrimaryIncrease) && (
          <div className="estimate-primary-from-to">
            <span>{fromTo}</span>
          </div>
        )}
        {(isDoublePrimary) && (
          <div className="second-primary">
            <div className="estimate-second-primary-titile neutral-estimate">
              <Textfit
                mode="single"
                forceSingleModeWidth={false}
                max={this.state.primaryAndSecondPrimaryFontSize}
                onReady={fontSize => this.secondPrimaryReady(fontSize)}
              >{secondPrimaryString}
              </Textfit>
            </div>
            <div className={`estimate-second-primary-increase neutral-estimate ${isLongText && 'long-text'}`}>
              <Textfit mode="single" forceSingleModeWidth={false}>{secondPrimaryIncrease}</Textfit>
            </div>
          </div>
        )}
        {secondaryIncrease && (
          <div className={`estimate-secondary ${isPositiveSecondary ? 'positive-estimate' : 'neutral-estimate'}`}>
            <span className="estimate-secondary-value">{this.getSecondarySymbol(secondaryIncrease)}{secondaryIncrease}</span>
            <span>&nbsp;</span>
            <span className="estimate-secondary-title">{secondaryString}</span>
          </div>
        )}
        {tertiaryIncrease && (
          <div className="estimate-tertiary positive-estimate">
            <span className="estimate-tertiary-value">+{tertiaryIncrease}</span>
            <span>&nbsp;</span>
            <span className="estimate-tertiary-title">{tertiaryString}</span>
          </div>
        )}
        {spendIncrease && (
          <div className="estimate-cost">
            <span className="estimate-cost-value">+{spendIncrease}</span>
            <span>&nbsp;</span>
            <span className="estimate-cost-title">{spendString}</span>
          </div>
        )}
        {isEstimate && (
          <div className="estimate-note">{this.props.estimateNote}</div>
        )}
      </div>
    );
  }
}

